<template>
    <div>
        <frontinus-data-table
          ref="dtNews"
          frontinusTitle="Zoznam aktualít"
          :frontinusItemsLoader="newsLoader"
          :headers="headers"
          :frontinusActionColumn="{ edit: true, delete: true }"
          show-expand
          @newItem="openDialog()"
          @editItem="openDialog($event)"
          @deleteItem="deleteItem($event)"
        >
            <template #expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-3">
                    <p class="font-weight-bold">{{ item.title }}</p>
                    {{ item.text }}
                </td>
            </template>

            <template #item.public="{ item }">
              <v-switch
                  :input-value="item.public"
                  @change="updateBooleanParameter(item, 'public', $event)"
                  :disabled="!item.text"
              ></v-switch>
            </template>

            <template #item.important="{ item }">
              <v-switch
                  :input-value="item.important"
                  @change="updateBooleanParameter(item, 'important', $event)"
              ></v-switch>
            </template>

            <template #item.text="{ item }">
              {{ item.text ? 'Áno' : 'Nie' }}
            </template>

            <template #customActions="{ item }">
              <v-icon class="mr-2" @click.stop="openRichEditor(item)">mdi-content-save-edit-outline</v-icon>
            </template>
        </frontinus-data-table>

        <form-dialog
            v-model="dialog.isOpen"
            :title="dialog.title"
            maxWidth="700px"
            @save="saveDialog"
        >
            <v-select
              v-model="dialog.model.type_id"
              :items="resources.types"
              label="* Kategória"
              item-text="name"
              item-value="id"
              :errorMessages="dialog.validationErrors.type_id"
              required
            ></v-select>

            <v-text-field
                v-model.trim="dialog.model.title"
                label="* Nadpis"
                :errorMessages="dialog.validationErrors.title"
                required
            ></v-text-field>

            <v-autocomplete
                v-model="dialog.model.author_id"
                label="* Autor"
                :errorMessages="dialog.validationErrors.author_id"
                :items="resources.users"
                item-text="name"
                item-value="id"
                required
            ></v-autocomplete>

            <v-file-input
                v-model="dialog.model.titleImage"
                label="* Titulná fotografia"
                :errorMessages="dialog.validationErrors.titleImage"
                required
            ></v-file-input>

            <v-text-field
                v-model.trim="dialog.model.titleImageCaption"
                label="Popis titulnej fotografie"
                :errorMessages="dialog.validationErrors.titleImageCaption"
                required
            ></v-text-field>

            <date-time-picker
              v-model="dialog.dateTimeIsOpen"
              :dateTime.sync="dialog.model.valid_to"
            >
              <template #activator="{ on }">
                <v-text-field
                  label="Zobraziť do"
                  :value="dialog.model.valid_to | dateTime"
                  :errorMessages="dialog.validationErrors.valid_to"
                  placeholder="Bez obmedzenia"
                  v-on="on"
                  clearable
                  readonly
                ></v-text-field>
              </template>
            </date-time-picker>
        </form-dialog>

        <rich-editor
          v-if="Object.keys(richEditor.saveContent).length"
          v-model="richEditor.isOpen"
          :initContent="richEditor.text"
          :saveUrl="richEditor.saveContent.url"
          :axiosConfig="richEditor.saveContent.baseConfig"
          @saved="onContentSaved"
        ></rich-editor>
    </div>
</template>

<script>
import Vue from 'vue'
import FrontinusDataTable from '@/components/FrontinusDataTable.vue'
import FormDialog from '@/components/FormDialog.vue'
import DateTimePicker from '@/components/DateTimePicker.vue'
import RichEditor from '@/components/RichEditor.vue'
import { NewsService } from '@/services/api/news.service'
import { PeopleService } from '@/services/api/people.service'
import { AuthService } from '@/services/api/auth.service'
import { FormMode } from '@/utils/FormMode.enum'
import { ValidationError } from '@/errors/validation.error'

export default Vue.extend({
  components: {
    FrontinusDataTable,
    FormDialog,
    DateTimePicker,
    RichEditor
  },

  data () {
    return {
      headers: [
        { text: 'Názov', value: 'title' },
        // { text: 'Text', value: 'text' },
        { text: 'Autor', value: 'author.name' },
        { text: 'Vytvorené', value: 'created_at' },
        { text: 'Publikovať', value: 'public' },
        { text: 'Hlavná správa', value: 'important' },
        { text: 'Zobraziť do', value: 'valid_to' },
        { text: 'Kategória', value: 'category.name' },
        { text: 'Zobrazené', value: 'visits_count' },
        { text: '', value: 'action', sortable: false }
      ],
      dialog: {
        isOpen: false,
        title: '',
        model: {},
        validationErrors: {},
        dateTimeIsOpen: false
      },
      richEditor: {
        isOpen: false,
        text: '',
        saveContent: {}
      },
      resources: {
        users: [],
        types: []
      }
    }
  },

  computed: {
    newsLoader () {
      return NewsService.list
    }
  },

  methods: {
    async initUsers () {
      const res = await PeopleService.list()
      this.resources.users = res.data
    },
    async initCategories () {
      const res = await NewsService.newsTypesList()
      this.resources.types = res.data
    },
    openDialog (model = null) {
      this.dialog.title = model ? 'Upraviť aktualitu' : 'Vytvoriť aktualitu'
      this.dialog.model = model ? { ...model } : { author_id: AuthService.activeUser.id }
      this.validationErrors = {}
      this.dialog.isOpen = true
      this.dialog.dateTimeIsOpen = false
    },
    async saveDialog () {
      try {
        await NewsService.createOrUpdate(this.dialog.model)
        this.$refs.dtNews.reload()
        this.dialog.isOpen = false
      } catch (e) {
        if (e instanceof ValidationError) {
          this.dialog.validationErrors = e.errors
        } else {
          throw e
        }
      }
    },
    async updateBooleanParameter (model, field, value) {
      model[field] = value
      try {
        await NewsService.update(model)
      } catch (e) {
        model[field] = !value
      }
    },
    openRichEditor (item) {
      this.richEditor.text = item.text
      this.richEditor.saveContent = NewsService.saveContent(item.slug)
      this.richEditor.isOpen = true
    },
    onContentSaved () {
      this.$refs.dtNews.reload()
      this.richEditor.isOpen = false
    },
    async deleteItem (model) {
      await NewsService.delete(model.slug)
      this.$refs.dtNews.reload()
    }
  },

  created () {
    this.initUsers()
    this.initCategories()
  }
})
</script>
